import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { isCreativeReviewDept } from "@/lib/dept-helpers";
import { AppHeader } from "@/components/app/AppHeader";
import { SubmissionsTable } from "@/components/submissions/SubmissionsTable";
import { SubmissionModal } from "@/components/submissions/SubmissionModal";
import { TaskUpdateModal } from "@/components/editor/TaskUpdateModal";
import { VideoSubmissionsTable } from "@/components/editor/VideoSubmissionsTable";
import { OtherTasksTable } from "@/components/editor/OtherTasksTable";
import { DesignerTaskUpdateModal } from "@/components/designer/DesignerTaskUpdateModal";
import { AiTaskUpdateModal } from "@/components/ai/AiTaskUpdateModal";
import { AiSubmissionsTable } from "@/components/ai/AiSubmissionsTable";
import { AiOtherTasksTable } from "@/components/ai/AiOtherTasksTable";
import { DesignerSubmissionsTable } from "@/components/designer/DesignerSubmissionsTable";
import { DesignerOtherTasksTable } from "@/components/designer/DesignerOtherTasksTable";
import { ScriptwriterTaskUpdateModal } from "@/components/scriptwriter/ScriptwriterTaskUpdateModal";
import { ScriptwriterSubmissionsTable } from "@/components/scriptwriter/ScriptwriterSubmissionsTable";
import { ScriptwriterOtherTasksTable } from "@/components/scriptwriter/ScriptwriterOtherTasksTable";
import { MarketerTaskUpdateModal } from "@/components/marketer/MarketerTaskUpdateModal";
import { MarketerTasksTable } from "@/components/marketer/MarketerTasksTable";
import { MarketerOtherTasksTable } from "@/components/marketer/MarketerOtherTasksTable";
import { SeoTaskUpdateModal } from "@/components/seo/SeoTaskUpdateModal";
import { SeoTasksTable } from "@/components/seo/SeoTasksTable";
import { SeoOtherTasksTable } from "@/components/seo/SeoOtherTasksTable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DateRangeFilter,
  EMPTY_RANGE,
  filterByDateField,
  type DateRange,
} from "@/components/common/DateRangeFilter";
import { useMySubmissions } from "@/hooks/use-my-submissions";
import { useMyOtherTasks, useMyVideoSubmissions } from "@/hooks/use-editor-tasks";
import { useMyDesignerOtherTasks, useMyDesignerSubmissions } from "@/hooks/use-designer-tasks";
import { useMyAiOtherTasks, useMyAiSubmissions } from "@/hooks/use-ai-tasks";
import { useMyScriptwriterOtherTasks, useMyScriptwriterSubmissions } from "@/hooks/use-scriptwriter-tasks";
import { useMyMarketerOtherTasks, useMyMarketerScripts, useMyMarketerTasks } from "@/hooks/use-marketer-tasks";
import { useMySeoOtherTasks, useMySeoTasks } from "@/hooks/use-seo-tasks";
import type { ScriptwriterOtherTask, ScriptwriterSubmission } from "@/lib/scriptwriter-tasks";
import { scriptToMarketerRow, type MarketerOtherTask, type MarketerTask, type MarketerTaskRow } from "@/lib/marketer-tasks";
import type { SeoOtherTask, SeoTask } from "@/lib/seo-tasks";
import { useAuth } from "@/lib/auth-context";
import { getDepartmentConfig } from "@/lib/department-configs";
import type { Submission } from "@/lib/submissions";
import type { EditorOtherTask, EditorVideoSubmission } from "@/lib/editor-tasks";
import type { DesignerOtherTask, DesignerSubmission } from "@/lib/designer-tasks";
import type { AiOtherTask, AiSubmission } from "@/lib/ai-tasks";
import { exportSubmissionsCsv } from "@/lib/export-csv";

export const Route = createFileRoute("/_app/my-submissions")({
  component: MySubmissionsPage,
});

function MySubmissionsPage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (profile && !isCreativeReviewDept(profile.department)) {
      toast.info("This section is only for Editors and Graphic Designer submissions.");
      navigate({ to: "/update-task" });
    }
  }, [profile, navigate]);
  if (!profile) return null;
  if (!isCreativeReviewDept(profile.department)) return null;
  if (profile.department === "editor") return <EditorView />;
  if (profile.department === "graphic_designer") return <DesignerView />;
  if (profile.department === "ai") return <AiView />;
  return <GenericView />;
}

function MarketerView() {
  const { data: tasks = [] } = useMyMarketerTasks();
  const { data: others = [] } = useMyMarketerOtherTasks();
  const { data: scripts = [] } = useMyMarketerScripts();
  const [editingTask, setEditingTask] = useState<MarketerTask | null>(null);
  const [editingOther, setEditingOther] = useState<MarketerOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const mergedTasks = useMemo<MarketerTaskRow[]>(() => {
    const all: MarketerTaskRow[] = [
      ...tasks.map((t) => ({ ...t, _source: "task" as const })),
      ...scripts.map(scriptToMarketerRow),
    ];
    return all.sort((a, b) => {
      const d = (b.task_date ?? "").localeCompare(a.task_date ?? "");
      if (d !== 0) return d;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [tasks, scripts]);
  const filteredTasks = useMemo(() => filterByDateField(mergedTasks, "task_date", range), [mergedTasks, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={tasks.length + others.length}
          filteredCount={filteredTasks.length + filteredOthers.length}
        />
        <Tabs defaultValue="task">
          <TabsList>
            <TabsTrigger value="task">Marketing Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            <MarketerTasksTable rows={filteredTasks} onEdit={setEditingTask} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <MarketerOtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <MarketerTaskUpdateModal
        open={!!editingTask || !!editingOther}
        onOpenChange={(o) => { if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther}
      />
    </>
  );
}

function SeoView() {
  const { data: tasks = [] } = useMySeoTasks();
  const { data: others = [] } = useMySeoOtherTasks();
  const [editingTask, setEditingTask] = useState<SeoTask | null>(null);
  const [editingOther, setEditingOther] = useState<SeoOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredTasks = useMemo(() => filterByDateField(tasks, "task_date", range), [tasks, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);
  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={tasks.length + others.length}
          filteredCount={filteredTasks.length + filteredOthers.length}
        />
        <Tabs defaultValue="task">
          <TabsList>
            <TabsTrigger value="task">SEO Tasks</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="task" className="mt-4">
            <SeoTasksTable rows={filteredTasks} onEdit={setEditingTask} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <SeoOtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <SeoTaskUpdateModal
        open={!!editingTask || !!editingOther}
        onOpenChange={(o) => { if (!o) { setEditingTask(null); setEditingOther(null); } }}
        editingTask={editingTask} editingOther={editingOther}
      />
    </>
  );
}

function ScriptwriterView() {
  const { data: scripts = [] } = useMyScriptwriterSubmissions();
  const { data: others = [] } = useMyScriptwriterOtherTasks();
  const [editingScript, setEditingScript] = useState<ScriptwriterSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<ScriptwriterOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredScripts = useMemo(() => filterByDateField(scripts, "submission_date", range), [scripts, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);

  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={scripts.length + others.length}
          filteredCount={filteredScripts.length + filteredOthers.length}
        />
        <Tabs defaultValue="script">
          <TabsList>
            <TabsTrigger value="script">Script Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="script" className="mt-4">
            <ScriptwriterSubmissionsTable rows={filteredScripts} onEdit={setEditingScript} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <ScriptwriterOtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <ScriptwriterTaskUpdateModal
        open={!!editingScript || !!editingOther}
        onOpenChange={(o) => { if (!o) { setEditingScript(null); setEditingOther(null); } }}
        editingScript={editingScript}
        editingOther={editingOther}
      />
    </>
  );
}

function DesignerView() {
  const { data: creatives = [] } = useMyDesignerSubmissions();
  const { data: others = [] } = useMyDesignerOtherTasks();
  const [editingCreative, setEditingCreative] = useState<DesignerSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<DesignerOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredCreatives = useMemo(() => filterByDateField(creatives, "submission_date", range), [creatives, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);

  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={creatives.length + others.length}
          filteredCount={filteredCreatives.length + filteredOthers.length}
        />
        <Tabs defaultValue="creative">
          <TabsList>
            <TabsTrigger value="creative">Creative Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="creative" className="mt-4">
            <DesignerSubmissionsTable rows={filteredCreatives} onEdit={setEditingCreative} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <DesignerOtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <DesignerTaskUpdateModal
        open={!!editingCreative || !!editingOther}
        onOpenChange={(o) => {
          if (!o) { setEditingCreative(null); setEditingOther(null); }
        }}
        editingCreative={editingCreative}
        editingOther={editingOther}
      />
    </>
  );
}

function AiView() {
  const { data: creatives = [] } = useMyAiSubmissions();
  const { data: others = [] } = useMyAiOtherTasks();
  const [editingCreative, setEditingCreative] = useState<AiSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<AiOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredCreatives = useMemo(() => filterByDateField(creatives, "submission_date", range), [creatives, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);

  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={creatives.length + others.length}
          filteredCount={filteredCreatives.length + filteredOthers.length}
        />
        <Tabs defaultValue="creative">
          <TabsList>
            <TabsTrigger value="creative">Creative Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="creative" className="mt-4">
            <AiSubmissionsTable rows={filteredCreatives} onEdit={setEditingCreative} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <AiOtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <AiTaskUpdateModal
        open={!!editingCreative || !!editingOther}
        onOpenChange={(o) => {
          if (!o) { setEditingCreative(null); setEditingOther(null); }
        }}
        editingCreative={editingCreative}
        editingOther={editingOther}
      />
    </>
  );
}

function EditorView() {
  const { data: videos = [] } = useMyVideoSubmissions();
  const { data: others = [] } = useMyOtherTasks();
  const [editingVideo, setEditingVideo] = useState<EditorVideoSubmission | null>(null);
  const [editingOther, setEditingOther] = useState<EditorOtherTask | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredVideos = useMemo(() => filterByDateField(videos, "submission_date", range), [videos, range]);
  const filteredOthers = useMemo(() => filterByDateField(others, "task_date", range), [others, range]);

  return (
    <>
      <AppHeader subtitle="A complete history of your work" />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={videos.length + others.length}
          filteredCount={filteredVideos.length + filteredOthers.length}
        />
        <Tabs defaultValue="video">
          <TabsList>
            <TabsTrigger value="video">Video Submissions</TabsTrigger>
            <TabsTrigger value="other">Other Tasks</TabsTrigger>
          </TabsList>
          <TabsContent value="video" className="mt-4">
            <VideoSubmissionsTable rows={filteredVideos} onEdit={setEditingVideo} />
          </TabsContent>
          <TabsContent value="other" className="mt-4">
            <OtherTasksTable rows={filteredOthers} onEdit={setEditingOther} />
          </TabsContent>
        </Tabs>
      </div>
      <TaskUpdateModal
        open={!!editingVideo || !!editingOther}
        onOpenChange={(o) => {
          if (!o) {
            setEditingVideo(null);
            setEditingOther(null);
          }
        }}
        editingVideo={editingVideo}
        editingOther={editingOther}
      />
    </>
  );
}

function GenericView() {
  const { profile } = useAuth();
  const { data: rows = [] } = useMySubmissions();
  const [editing, setEditing] = useState<Submission | null>(null);
  const [range, setRange] = useState<DateRange>(EMPTY_RANGE);
  const filteredRows = useMemo(
    () => filterByDateField(rows, "submission_date", range),
    [rows, range],
  );

  if (!profile) return null;
  const config = getDepartmentConfig(profile.department);

  return (
    <>
      <AppHeader
        subtitle="A complete history of your work"
        onExport={() => exportSubmissionsCsv(filteredRows, "my-submissions.csv", { includePoints: profile?.department === "editor" })}
      />
      <div className="px-[22px] pb-6">
        <DateRangeFilter
          value={range}
          onChange={setRange}
          totalCount={rows.length}
          filteredCount={filteredRows.length}
        />
        <SubmissionsTable rows={filteredRows} config={config} onEdit={setEditing} />
      </div>
      <SubmissionModal
        open={!!editing}
        onOpenChange={(o: boolean) => !o && setEditing(null)}
        config={config}
        editing={editing}
      />
    </>
  );
}
